import {
  collection,
  doc,
  getDocs,
  getDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  setDoc,
  query,
  where,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Member, Election, Vote, Candidate, VoteGroup, Admin } from '../types';

// Service pour gérer les membres
export class MemberService {
  private static collection = 'members';

  static async getAllMembers(): Promise<Member[]> {
    const snapshot = await getDocs(collection(db, this.collection));
    return snapshot.docs.map(doc => ({
      id: doc.data().email, // Utiliser l'email comme ID
      ...doc.data(),
      memberSince: doc.data().memberSince?.toDate(),
      lastActive: doc.data().lastActive?.toDate(),
    })) as Member[];
  }

  static async getMemberById(id: string): Promise<Member | null> {
    const docRef = doc(db, this.collection, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: data.email, // Utiliser l'email comme ID
        ...data,
        memberSince: data.memberSince?.toDate(),
        lastActive: data.lastActive?.toDate(),
      } as Member;
    }
    return null;
  }

  static async getActiveMembers(): Promise<Member[]> {
    const q = query(
      collection(db, this.collection),
      where('membershipStatus', '==', 'active'),
      where('hasVotingRights', '==', true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.data().email, // Utiliser l'email comme ID
      ...doc.data(),
      memberSince: doc.data().memberSince?.toDate(),
      lastActive: doc.data().lastActive?.toDate(),
    })) as Member[];
  }

  static async createMember(memberData: Omit<Member, 'id'>): Promise<string> {
    // Utiliser l'email comme ID de document
    const docRef = doc(db, this.collection, memberData.email);
    await setDoc(docRef, {
      ...memberData,
      memberSince: Timestamp.fromDate(memberData.memberSince),
      lastActive: Timestamp.fromDate(memberData.lastActive),
    });
    return memberData.email; // Retourner l'email comme ID
  }

  static async updateMemberStatus(email: string, status: 'active' | 'inactive' | 'paused'): Promise<void> {
    const docRef = doc(db, this.collection, email);
    await updateDoc(docRef, {
      membershipStatus: status,
      hasVotingRights: status === 'active',
      lastActive: Timestamp.now()
    });
  }

  static async deleteMember(email: string): Promise<void> {
    const docRef = doc(db, this.collection, email);
    await deleteDoc(docRef);
  }

  static async getMemberByEmail(email: string): Promise<Member | null> {
    // Utiliser directement l'email comme ID de document
    const docRef = doc(db, this.collection, email);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: data.email, // Utiliser l'email comme ID
        ...data,
        memberSince: data.memberSince?.toDate(),
        lastActive: data.lastActive?.toDate(),
      } as Member;
    }
    return null;
  }

  static async updateMember(email: string, memberData: Partial<Member>): Promise<void> {
    const docRef = doc(db, this.collection, email);
    const updateData: any = { ...memberData };
    
    if (memberData.memberSince) {
      updateData.memberSince = Timestamp.fromDate(memberData.memberSince);
    }
    if (memberData.lastActive) {
      updateData.lastActive = Timestamp.fromDate(memberData.lastActive);
    }
    
    await updateDoc(docRef, updateData);
  }
}

// Service pour gérer les élections
export class ElectionService {
  private static collection = 'elections';

  static async getAllElections(): Promise<Election[]> {
    const snapshot = await getDocs(collection(db, this.collection));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate(),
      endDate: doc.data().endDate?.toDate(),
    })) as Election[];
  }

  static async getActiveElections(): Promise<Election[]> {
    const q = query(
      collection(db, this.collection),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate(),
      endDate: doc.data().endDate?.toDate(),
    })) as Election[];
  }

  static async createElection(election: Omit<Election, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.collection), {
      ...election,
      startDate: Timestamp.fromDate(election.startDate),
      endDate: Timestamp.fromDate(election.endDate),
    });
    return docRef.id;
  }

  static async getElectionById(id: string): Promise<Election | null> {
    const docRef = doc(db, this.collection, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
      } as Election;
    }
    return null;
  }

  static async updateElection(id: string, updates: Partial<Election>): Promise<void> {
    const docRef = doc(db, this.collection, id);
    const updateData: any = { ...updates };
    
    if (updates.startDate) {
      updateData.startDate = Timestamp.fromDate(updates.startDate);
    }
    if (updates.endDate) {
      updateData.endDate = Timestamp.fromDate(updates.endDate);
    }
    
    await updateDoc(docRef, updateData);
  }

  static async deleteElection(id: string): Promise<void> {
    const docRef = doc(db, this.collection, id);
    await deleteDoc(docRef);
  }
}

// Service pour gérer les groupes d'élections
export class VoteGroupService {
  private static collection = 'voteGroups';

  static async getAllVoteGroups(): Promise<VoteGroup[]> {
    const querySnapshot = await getDocs(collection(db, this.collection));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate(),
      endDate: doc.data().endDate?.toDate(),
    })) as VoteGroup[];
  }

  static async getVoteGroupById(id: string): Promise<VoteGroup | null> {
    const docRef = doc(db, this.collection, id);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const data = docSnap.data();
      return {
        id: docSnap.id,
        ...data,
        startDate: data.startDate?.toDate(),
        endDate: data.endDate?.toDate(),
      } as VoteGroup;
    }
    return null;
  }

  static async createVoteGroup(voteGroup: Omit<VoteGroup, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.collection), {
      ...voteGroup,
      startDate: Timestamp.fromDate(voteGroup.startDate),
      endDate: Timestamp.fromDate(voteGroup.endDate),
    });
    return docRef.id;
  }

  static async updateVoteGroup(id: string, updates: Partial<VoteGroup>): Promise<void> {
    const docRef = doc(db, this.collection, id);
    const updateData: any = { ...updates };

    if (updates.startDate) {
      updateData.startDate = Timestamp.fromDate(updates.startDate);
    }
    if (updates.endDate) {
      updateData.endDate = Timestamp.fromDate(updates.endDate);
    }

    await updateDoc(docRef, updateData);
  }

  static async getActiveVoteGroup(): Promise<VoteGroup | null> {
    const now = new Date();
    const q = query(
      collection(db, this.collection),
      where('isActive', '==', true),
      where('startDate', '<=', Timestamp.fromDate(now)),
      where('endDate', '>=', Timestamp.fromDate(now))
    );
    
    const snapshot = await getDocs(q);
    if (snapshot.empty) {
      return null;
    }

    // Retourner le groupe avec la date de fin la plus récente
    const groups = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      startDate: doc.data().startDate?.toDate(),
      endDate: doc.data().endDate?.toDate(),
    })) as VoteGroup[];

    return groups.sort((a, b) => b.endDate.getTime() - a.endDate.getTime())[0];
  }

  static async deleteVoteGroup(id: string): Promise<void> {
    const docRef = doc(db, this.collection, id);
    await deleteDoc(docRef);
  }
}

// Service pour gérer les votes
export class VoteService {
  private static collection = 'votes';

  static async createVote(vote: Omit<Vote, 'id' | 'timestamp'>): Promise<string> {
    const voteData: any = {
      memberId: vote.memberId,
      electionId: vote.electionId,
      isAbstention: vote.isAbstention,
      timestamp: Timestamp.now(),
    };

    // Ajouter les champs optionnels
    if (vote.groupId) {
      voteData.groupId = vote.groupId;
    }
    if (vote.candidateId) {
      voteData.candidateId = vote.candidateId;
    }
    if (vote.ipAddress) {
      voteData.ipAddress = vote.ipAddress;
    }
    if (vote.comment) {
      voteData.comment = vote.comment;
    }

    const docRef = await addDoc(collection(db, this.collection), voteData);
    return docRef.id;
  }

  static async getVotesByMember(memberId: string): Promise<Vote[]> {
    const q = query(
      collection(db, this.collection),
      where('memberId', '==', memberId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate(),
    })) as Vote[];
  }

  static async hasMemberVoted(memberId: string, electionId: string): Promise<boolean> {
    const q = query(
      collection(db, this.collection),
      where('memberId', '==', memberId),
      where('electionId', '==', electionId)
    );
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  }

  static async createMultipleVotes(votes: Array<Omit<Vote, 'id' | 'timestamp'>>): Promise<string[]> {
    const batch = writeBatch(db);
    const voteIds: string[] = [];

    votes.forEach(vote => {
      // Vérifier que les champs requis ne sont pas undefined
      if (!vote.memberId || !vote.electionId) {
        throw new Error(`Champs requis manquants: memberId=${vote.memberId}, electionId=${vote.electionId}`);
      }

      const docRef = doc(collection(db, this.collection));
      
      // Créer l'objet de données en filtrant les valeurs undefined
      const voteData: any = {
        memberId: vote.memberId,
        electionId: vote.electionId,
        isAbstention: vote.isAbstention,
        timestamp: Timestamp.now(),
      };

      // Ajouter les champs optionnels seulement s'ils sont définis
      if (vote.groupId) {
        voteData.groupId = vote.groupId;
      }
      if (vote.candidateId) {
        voteData.candidateId = vote.candidateId;
      }
      if (vote.ipAddress) {
        voteData.ipAddress = vote.ipAddress;
      }
      if (vote.comment) {
        voteData.comment = vote.comment;
      }

      batch.set(docRef, voteData);
      voteIds.push(docRef.id);
    });

    await batch.commit();
    return voteIds;
  }

  static async getVotesByGroup(groupId: string): Promise<Vote[]> {
    const q = query(
      collection(db, this.collection),
      where('groupId', '==', groupId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate(),
    })) as Vote[];
  }

  static async getAllVotes(): Promise<Vote[]> {
    const snapshot = await getDocs(collection(db, this.collection));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate(),
    })) as Vote[];
  }

  static async getVotesByElection(electionId: string): Promise<Vote[]> {
    const q = query(
      collection(db, this.collection),
      where('electionId', '==', electionId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate(),
    })) as Vote[];
  }

  static async deleteVotesByGroup(groupId: string): Promise<number> {
    const q = query(
      collection(db, this.collection),
      where('groupId', '==', groupId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    return snapshot.docs.length;
  }

  static async deleteVotesByElection(electionId: string): Promise<number> {
    const q = query(
      collection(db, this.collection),
      where('electionId', '==', electionId)
    );
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    return snapshot.docs.length;
  }

  static async deleteAllVotes(): Promise<number> {
    const snapshot = await getDocs(collection(db, this.collection));
    
    if (snapshot.empty) {
      return 0;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    await batch.commit();
    return snapshot.docs.length;
  }
}

// Service pour gérer les candidats
export class CandidateService {
  private static collection = 'candidates';

  static async getAllCandidates(): Promise<Candidate[]> {
    const snapshot = await getDocs(collection(db, this.collection));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Candidate[];
  }

  static async getCandidatesByPosition(position: string): Promise<Candidate[]> {
    const q = query(
      collection(db, this.collection),
      where('position', '==', position),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    })) as Candidate[];
  }

  static async createCandidate(candidate: Omit<Candidate, 'id'>): Promise<string> {
    const docRef = await addDoc(collection(db, this.collection), candidate);
    return docRef.id;
  }
}

// Service pour gérer les administrateurs
export class AdminService {
  private static collection = 'admins';

  static async getAdminById(adminId: string): Promise<Admin | null> {
    const docRef = doc(db, this.collection, adminId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data(),
        createdAt: docSnap.data().createdAt?.toDate() || new Date(),
      } as Admin;
    }
    return null;
  }

  static async getAdminByEmail(email: string): Promise<Admin | null> {
    const q = query(
      collection(db, this.collection),
      where('email', '==', email)
    );
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const doc = snapshot.docs[0];
      return {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate() || new Date(),
      } as Admin;
    }
    return null;
  }

  static async updateAdminPhoto(adminId: string, photoUrl: string): Promise<void> {
    const docRef = doc(db, this.collection, adminId);
    await updateDoc(docRef, {
      photo: photoUrl,
      updatedAt: Timestamp.now()
    });
  }

  static async removeAdminPhoto(adminId: string): Promise<void> {
    const docRef = doc(db, this.collection, adminId);
    await updateDoc(docRef, {
      photo: null,
      updatedAt: Timestamp.now()
    });
  }

  static async updateAdmin(adminId: string, updates: Partial<Admin>): Promise<void> {
    const docRef = doc(db, this.collection, adminId);
    const updateData: any = {
      ...updates,
      updatedAt: Timestamp.now()
    };
    
    // Convertir les dates en Timestamp si nécessaire
    if (updates.createdAt) {
      updateData.createdAt = Timestamp.fromDate(updates.createdAt);
    }
    
    await updateDoc(docRef, updateData);
  }

  static async createAdmin(admin: Omit<Admin, 'id'>): Promise<string> {
    const adminData = {
      ...admin,
      createdAt: Timestamp.fromDate(admin.createdAt),
      updatedAt: Timestamp.now()
    };
    
    const docRef = await addDoc(collection(db, this.collection), adminData);
    return docRef.id;
  }

  static async getAllAdmins(): Promise<Admin[]> {
    const snapshot = await getDocs(collection(db, this.collection));
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate() || new Date(),
    })) as Admin[];
  }
}
